import NumberUnit, { isNumberUnit } from '@exodus/currency';
import assert from 'minimalistic-assert';
import lodash from 'lodash';
import { ORDER_TYPES, ORDER_STATUS, STATUS_MAP } from './constants.js';
import { serialize, deserialize } from '../account-state/index.js';
import { isOrderAmountLegacyDefaultUnitString, isOrderAmountLegacyNumber } from './utils.js';
import { ModelIdSymbol } from '../constants.js';
import { createIsInstance, omitUndefined } from '../utils.js';
const { isEqual } = lodash;
const coerceToObject = (obj) => obj instanceof FiatOrder ? obj.toJSON() : obj;
export const fusionOrderToOrderAdapter = (order, assets) => {
    if (!assets) {
        throw new Error('assets is required');
    }
    const result = { ...order };
    const isBuy = order.orderType === ORDER_TYPES.buy;
    const cryptoAsset = isBuy ? order.toAsset : order.fromAsset;
    const cryptoAmount = isBuy ? order.toAmount : order.fromAmount;
    const cryptoCurrency = assets[cryptoAsset].currency;
    const serializedCryptoAmount = !cryptoAmount || isOrderAmountLegacyNumber(cryptoAmount)
        ? serialize(cryptoCurrency.defaultUnit(cryptoAmount ?? 0))
        : isOrderAmountLegacyDefaultUnitString(cryptoAmount)
            ? serialize(cryptoCurrency.parse(cryptoAmount))
            : cryptoAmount;
    result.fromAmount = isBuy ? order.fromAmount : serializedCryptoAmount;
    result.toAmount = isBuy ? serializedCryptoAmount : order.toAmount;
    return result;
};
export const adapter = ({ createdAt, fee = 0, from, id, networkFee = 0, paymentMethodType, provider, providerId, rate, status, subProvider, subProviderId, to, totalFee = 0, txId, type, walletAccount, walletAddress, }, assets) => {
    if (!assets) {
        throw new Error('assets is required');
    }
    const isBuy = type === ORDER_TYPES.buy;
    const fiat = isBuy ? from : to;
    const crypto = isBuy ? to : from;
    const cryptoAsset = assets[crypto.asset];
    const cryptoAmount = serialize(cryptoAsset.currency.defaultUnit(crypto?.amount || 0));
    return {
        date: createdAt,
        exodusRate: rate,
        fees: {
            networkFee,
            providerFee: fee,
            processingFee: 0,
            totalFee,
        },
        fiatValue: fiat.amount,
        fromAddress: isBuy ? null : walletAddress,
        fromAmount: isBuy ? fiat.amount : cryptoAmount,
        fromAsset: from.asset,
        fromWalletAccount: isBuy ? null : walletAccount,
        orderId: id,
        orderType: type,
        paymentMethodType,
        provider,
        providerOrderId: providerId,
        providerRate: rate,
        subProvider,
        subProviderOrderId: subProviderId,
        status,
        toAddress: isBuy ? walletAddress : null,
        toAmount: isBuy ? cryptoAmount : fiat.amount,
        toAsset: to.asset,
        toWalletAccount: isBuy ? walletAccount : null,
        txId,
    };
};
const FACTORY_SYMBOL = Symbol('FiatOrder');
export default class FiatOrder {
    errorMessage;
    provider;
    status;
    date;
    exodusRate;
    fiatValue;
    fromAddress;
    fromAmount;
    fromAsset;
    fromWalletAccount;
    orderId;
    orderType;
    providerOrderId;
    providerRate;
    subProvider;
    subProviderOrderId;
    toAddress;
    toAmount;
    toAsset;
    toWalletAccount;
    txId;
    fees;
    feesWereSubsidized;
    hideInHistory;
    showedFailureBanner;
    supportMessage;
    paymentMethodType;
    constructor(props, initSymbol) {
        FiatOrder.validate(props, initSymbol);
        Object.assign(this, props);
        if (this.exodusStatus === ORDER_STATUS.complete && this.errorMessage) {
            delete this.errorMessage;
        }
    }
    static get [ModelIdSymbol]() {
        return 'FiatOrder';
    }
    static _isInstance = createIsInstance(FiatOrder);
    static [Symbol.hasInstance](x) {
        return this._isInstance(x);
    }
    static isInstance = FiatOrder[Symbol.hasInstance];
    #matches(fields) {
        const props = Object.keys(fields);
        return props.every((key) => {
            const value = fields[key];
            if (value instanceof Date) {
                return this[key]?.toISOString() === value.toISOString();
            }
            if (isNumberUnit(value)) {
                return this[key]?.equals(value);
            }
            return isEqual(this[key], fields[key]);
        });
    }
    static validate = (order, initSymbol) => {
        assert(initSymbol === FACTORY_SYMBOL, 'please use Order.fromJSON()');
        assert(typeof order.orderId === 'string', 'expected string "orderId"');
        assert(['buy', 'sell'].includes(order.orderType), 'expected "orderType" of value "buy" | "sell"');
        assert(typeof order.provider === 'string' && STATUS_MAP[order.provider], 'invalid provider');
        assert(typeof order.status === 'string', 'expected string "status"');
        assert(typeof order.fromAddress === 'string' || order.fromAddress === null, 'expected string or null "fromAddress"');
        assert(typeof order.fromWalletAccount === 'string' || order.fromWalletAccount === null, 'expected string or null "fromWalletAccount"');
        assert(typeof order.fromAmount === 'number' || order.fromAmount instanceof NumberUnit, 'expected number or NumberUnit "fromAmount"');
        assert(typeof order.fromAsset === 'string', 'expected string "fromAsset"');
        assert(typeof order.toAddress === 'string' || order.toAddress === null, 'expected string or null "toAddress"');
        assert(typeof order.toWalletAccount === 'string' || order.toWalletAccount === null, 'expected string or null "toWalletAccount"');
        assert(typeof order.toAmount === 'number' || order.toAmount instanceof NumberUnit, 'expected number or NumberUnit "toAmount"');
        assert(typeof order.toAsset === 'string', 'expected string "toAsset"');
        assert(typeof order.fiatValue === 'number', 'expected number "fiatValue"');
        assert(typeof order.providerRate === 'number', 'expected number "providerRate"');
        assert(typeof order.exodusRate === 'number', 'expected number "exodusRate"');
        if (order.feesWereSubsidized !== undefined) {
            assert(typeof order.feesWereSubsidized === 'boolean', 'expected boolean "feesWereSubsidized"');
        }
        if (order.errorMessage) {
            assert(typeof order.errorMessage === 'string', 'expected string "errorMessage"');
        }
        if (order.fees) {
            assert(typeof order.fees === 'object', 'expected object "fees"');
        }
        if (order.hideInHistory !== undefined) {
            assert(typeof order.hideInHistory === 'boolean', 'expected boolean "hideInHistory"');
        }
        if (order.supportMessage != null) {
            assert(typeof order.supportMessage === 'string', 'expected string "supportMessage"');
        }
        if (order.showedFailureBanner !== undefined) {
            assert(typeof order.showedFailureBanner === 'boolean', 'expected boolean "showedFailureBanner"');
        }
    };
    get exodusStatus() {
        if (this.status === 'completed') {
            return ORDER_STATUS.complete;
        }
        if (Object.values(ORDER_STATUS).includes(this.status)) {
            return this.status;
        }
        const providerLegacyStatuses = STATUS_MAP[this.provider] ?? {};
        const match = Object.entries(providerLegacyStatuses).find(([, providerStatuses]) => providerStatuses.includes(this.status));
        return match ? match[0] : ORDER_STATUS.in_progress;
    }
    get error() {
        return !!this.errorMessage;
    }
    get isBuy() {
        return this.orderType === 'buy';
    }
    get isSell() {
        return this.orderType === 'sell';
    }
    get cryptoAmount() {
        return this.isBuy ? this.toAmount : this.fromAmount;
    }
    get cryptoAsset() {
        return this.isBuy ? this.toAsset : this.fromAsset;
    }
    get txIds() {
        return this.txId ? [this.txId] : [];
    }
    static fromJSON(order) {
        const isBuy = order.orderType === ORDER_TYPES.buy;
        return new FiatOrder({
            ...order,
            date: order.date instanceof Date ? order.date : new Date(order.date),
            ...(isBuy
                ? { toAmount: deserialize(order.toAmount) }
                : { fromAmount: deserialize(order.fromAmount) }),
        }, FACTORY_SYMBOL);
    }
    equals(fields) {
        const data = coerceToObject(fields);
        const fieldsToCheck = new Set(Object.keys(data));
        const existingFields = new Set(Object.keys(this.toJSON()));
        const keysMatch = existingFields.size === fieldsToCheck.size &&
            [...existingFields].every((field) => fieldsToCheck.has(field));
        return keysMatch ? this.#matches(fields) : false;
    }
    inspect() {
        return `[${this.constructor.name} ${this.orderId}]`;
    }
    toJSON() {
        const json = {
            ...this,
            date: this.date.toISOString(),
            ...(this.isBuy
                ? { toAmount: serialize(this.toAmount) }
                : { fromAmount: serialize(this.fromAmount) }),
        };
        return omitUndefined(json);
    }
    toRedactedJSON() {
        return omitUndefined({
            cryptoAmount: isNumberUnit(this.cryptoAmount)
                ? serialize(this.cryptoAmount)
                : this.cryptoAmount,
            cryptoAsset: this.cryptoAsset,
            date: this.date.toISOString(),
            exodusRate: this.exodusRate,
            exodusStatus: this.exodusStatus,
            fees: this.fees,
            fiatValue: this.fiatValue,
            fromAmount: isNumberUnit(this.fromAmount) ? serialize(this.fromAmount) : this.fromAmount,
            fromAsset: this.fromAsset,
            isBuy: this.isBuy,
            isSell: this.isSell,
            orderId: this.orderId,
            orderType: this.orderType,
            paymentMethodType: this.paymentMethodType,
            provider: this.provider,
            providerOrderId: this.providerOrderId,
            providerRate: this.providerRate,
            subProvider: this.subProvider,
            subProviderOrderId: this.subProviderOrderId,
            status: this.status,
            toAddress: this.toAddress,
            toAmount: isNumberUnit(this.toAmount) ? serialize(this.toAmount) : this.toAmount,
            toAsset: this.toAsset,
            toWalletAccount: this.toWalletAccount,
            txIds: this.txIds,
        });
    }
    toString() {
        return this.orderId;
    }
    update(fields) {
        const data = coerceToObject(fields);
        if (this.#matches(data)) {
            return this;
        }
        return FiatOrder.fromJSON({ ...this.toJSON(), ...data });
    }
}
